import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import { trackingService, materialSettingsService } from '../services/api.service';

const Tracking = ({ user }) => {
  const [activeTab, setActiveTab] = useState('stock'); // 'stock' or 'machine'
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  return (
    <div style={{ padding: '2rem', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: COLORS.primary, marginBottom: '0.5rem' }}>📍 Takip</h1>
        <p style={{ color: '#666' }}>Stok durumu ve makina üretim takibi</p>
      </div>

      {message.text && (
        <div style={{
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b'
        }}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div style={{ marginBottom: '2rem', borderBottom: '2px solid #e5e7eb' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setActiveTab('stock')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              borderBottom: activeTab === 'stock' ? '3px solid #E40A04' : '3px solid transparent',
              background: activeTab === 'stock' ? '#fff' : 'transparent',
              color: activeTab === 'stock' ? COLORS.primary : '#666',
              fontWeight: activeTab === 'stock' ? '600' : '400',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.2s'
            }}
          >
            📦 Stok Durumu
          </button>
          <button
            onClick={() => setActiveTab('machine')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              borderBottom: activeTab === 'machine' ? '3px solid #E40A04' : '3px solid transparent',
              background: activeTab === 'machine' ? '#fff' : 'transparent',
              color: activeTab === 'machine' ? COLORS.primary : '#666',
              fontWeight: activeTab === 'machine' ? '600' : '400',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.2s'
            }}
          >
            🏭 Makina Üretimi
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'stock' ? (
        <StockStatusTab user={user} setMessage={setMessage} />
      ) : (
        <MachineProductionTab user={user} setMessage={setMessage} />
      )}
    </div>
  );
};

// Stock Status Tab Component
const StockStatusTab = ({ user, setMessage }) => {
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [allStockData, setAllStockData] = useState([]);
  const [summary, setSummary] = useState({ total_materials: 0, mevcut: 0, azaldi: 0, bitti: 0 });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [materialSettings, setMaterialSettings] = useState([]);
  const [editingMaterial, setEditingMaterial] = useState(null);
  
  const [filters, setFilters] = useState({
    material_name: '',
    material_type: '',
    material_quality: '',
    stock_status: ''
  });

  useEffect(() => {
    loadStockStatus();
    loadMaterialSettings();
  }, []);

  const loadStockStatus = async () => {
    setLoading(true);
    try {
      const response = await trackingService.getStockStatus(filters);
      setStockData(response.data || []);
      setSummary(response.summary || { total_materials: 0, mevcut: 0, azaldi: 0, bitti: 0 });
      
      if (!filters.material_name && !filters.material_type && !filters.material_quality && !filters.stock_status) {
        setAllStockData(response.data || []);
      }
      
      setMessage({ type: 'success', text: 'Stok durumu yüklendi' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Stok durumu yüklenemedi!' });
      console.error('Stok durumu hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMaterialSettings = async () => {
    try {
      const data = await materialSettingsService.getSettings();
      setMaterialSettings(data || []);
    } catch (error) {
      console.error('Malzeme ayarları yüklenemedi:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadStockStatus();
  };

  const clearFilters = () => {
    setFilters({
      material_name: '',
      material_type: '',
      material_quality: '',
      stock_status: ''
    });
    setTimeout(() => loadStockStatus(), 100);
  };

  const uniqueMaterialNames = [...new Set(allStockData.map(item => item.material_name))].sort();

  const handleEditSettings = (material) => {
    setEditingMaterial(material);
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    if (!editingMaterial) return;
    
    try {
      await materialSettingsService.updateSetting(
        editingMaterial.material_name,
        {
          minimum_stock_level: editingMaterial.minimum_stock_level,
          required_amount: editingMaterial.required_amount
        },
        editingMaterial.material_type,
        editingMaterial.material_quality
      );
      
      setMessage({ type: 'success', text: 'Ayarlar güncellendi!' });
      setShowSettingsModal(false);
      setEditingMaterial(null);
      loadStockStatus();
      loadMaterialSettings();
    } catch (error) {
      setMessage({ type: 'error', text: 'Ayarlar güncellenemedi!' });
      console.error('Ayar hatası:', error);
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const blob = await trackingService.exportExcel(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stok_durumu_${new Date().getTime()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Excel dosyası indirildi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Excel oluşturulamadı!' });
      console.error('Excel export error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setLoading(true);
      const blob = await trackingService.exportPdf(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stok_durumu_${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'PDF dosyası indirildi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'PDF oluşturulamadı!' });
      console.error('PDF export error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'MEVCUT': return '#10b981';
      case 'AZALDI': return '#f59e0b';
      case 'BİTTİ': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Malzeme</p>
          <h2 style={{ color: COLORS.primary, margin: 0 }}>{summary.total_materials}</h2>
        </div>
        <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>✓ Mevcut</p>
          <h2 style={{ color: '#10b981', margin: 0 }}>{summary.mevcut}</h2>
        </div>
        <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>⚠ Azaldı</p>
          <h2 style={{ color: '#f59e0b', margin: 0 }}>{summary.azaldi}</h2>
        </div>
        <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>✗ Bitti</p>
          <h2 style={{ color: '#ef4444', margin: 0 }}>{summary.bitti}</h2>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>🔍 Filtreler</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600' }}>Malzeme Adı</label>
            <select value={filters.material_name} onChange={(e) => handleFilterChange('material_name', e.target.value)} style={inputStyle}>
              <option value="">Tümü</option>
              {uniqueMaterialNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Stok Durumu</label>
            <select value={filters.stock_status} onChange={(e) => handleFilterChange('stock_status', e.target.value)} style={inputStyle}>
              <option value="">Tümü</option>
              <option value="MEVCUT">✓ Mevcut</option>
              <option value="AZALDI">⚠ Azaldı</option>
              <option value="BİTTİ">✗ Bitti</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Malzeme Cinsi</label>
            <select value={filters.material_type} onChange={(e) => handleFilterChange('material_type', e.target.value)} style={inputStyle}>
              <option value="">Tümü</option>
              <option value="L">L</option>
              <option value="R">R</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Malzeme Kalitesi</label>
            <input
              type="text"
              value={filters.material_quality}
              onChange={(e) => handleFilterChange('material_quality', e.target.value)}
              placeholder="Örn: S355JR, S235JR"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button onClick={applyFilters} style={primaryButtonStyle}>
              {loading ? '🔄 Yükleniyor...' : '🔍 Filtrele'}
            </button>
            <button onClick={clearFilters} style={secondaryButtonStyle}>
              ❌ Temizle
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              onClick={handleExportExcel} 
              disabled={loading || stockData.length === 0}
              style={{
                ...exportButtonStyle,
                background: stockData.length === 0 ? '#ccc' : '#10b981',
                cursor: stockData.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              📊 Excel İndir
            </button>
            <button 
              onClick={handleExportPdf}
              disabled={loading || stockData.length === 0}
              style={{
                ...exportButtonStyle,
                background: stockData.length === 0 ? '#ccc' : '#ef4444',
                cursor: stockData.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              📄 PDF İndir
            </button>
          </div>
        </div>
      </div>

      {/* Stock Status Table */}
      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: COLORS.secondary, color: '#fff' }}>
                <th style={tableHeaderStyle}>Malzeme Adı</th>
                <th style={tableHeaderStyle}>Cinsi</th>
                <th style={tableHeaderStyle}>Kalite</th>
                <th style={tableHeaderStyle}>Birim</th>
                <th style={tableHeaderStyle}>Giriş Mik.</th>
                <th style={tableHeaderStyle}>Çıkan Mik.</th>
                <th style={tableHeaderStyle}>Stok Mik.</th>
                <th style={tableHeaderStyle}>Gerekli Mal.</th>
                <th style={tableHeaderStyle}>Min. Stok</th>
                <th style={tableHeaderStyle}>Stok Durumu</th>
                {user?.role === 'admin' && <th style={tableHeaderStyle}>İşlem</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    Yükleniyor...
                  </td>
                </tr>
              ) : stockData.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    Veri bulunamadı
                  </td>
                </tr>
              ) : (
                stockData.map((item, index) => (
                  <tr key={index} style={{ 
                    borderBottom: '1px solid #e5e7eb',
                    background: index % 2 === 0 ? '#fff' : '#f9fafb'
                  }}>
                    <td style={tableCellStyle}>{item.material_name}</td>
                    <td style={tableCellStyle}>{item.material_type || '-'}</td>
                    <td style={tableCellStyle}>{item.material_quality || '-'}</td>
                    <td style={tableCellStyle}>{item.unit}</td>
                    <td style={tableCellStyle}>{item.total_in.toFixed(2)}</td>
                    <td style={tableCellStyle}>{item.total_out.toFixed(2)}</td>
                    <td style={{...tableCellStyle, fontWeight: '600', color: item.current_stock <= 0 ? '#ef4444' : '#000'}}>
                      {item.current_stock.toFixed(2)}
                    </td>
                    <td style={tableCellStyle}>{item.required_amount.toFixed(2)}</td>
                    <td style={tableCellStyle}>{item.minimum_stock_level.toFixed(2)}</td>
                    <td style={{...tableCellStyle, color: getStatusColor(item.stock_status), fontWeight: '600'}}>
                      {item.status_indicator} {item.stock_status}
                    </td>
                    {user?.role === 'admin' && (
                      <td style={tableCellStyle}>
                        <button
                          onClick={() => handleEditSettings(item)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#6366f1',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          ⚙️ Ayarla
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && editingMaterial && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ marginTop: 0, color: COLORS.primary }}>Malzeme Ayarları</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              <strong>{editingMaterial.material_name}</strong> 
              {editingMaterial.material_type && ` - ${editingMaterial.material_type}`}
              {editingMaterial.material_quality && ` - ${editingMaterial.material_quality}`}
            </p>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Minimum Stok Seviyesi (kg)
              </label>
              <input
                type="number"
                step="0.01"
                value={editingMaterial.minimum_stock_level}
                onChange={(e) => setEditingMaterial({
                  ...editingMaterial,
                  minimum_stock_level: parseFloat(e.target.value) || 0
                })}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Gerekli Malzeme Miktarı (kg)
              </label>
              <input
                type="number"
                step="0.01"
                value={editingMaterial.required_amount}
                onChange={(e) => setEditingMaterial({
                  ...editingMaterial,
                  required_amount: parseFloat(e.target.value) || 0
                })}
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={handleSaveSettings} style={primaryButtonStyle}>
                💾 Kaydet
              </button>
              <button 
                onClick={() => {
                  setShowSettingsModal(false);
                  setEditingMaterial(null);
                }}
                style={secondaryButtonStyle}
              >
                ❌ İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Machine Production Tab Component
const MachineProductionTab = ({ user, setMessage }) => {
  const [loading, setLoading] = useState(false);
  const [machineData, setMachineData] = useState(null);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    makina: '',
    operator: ''
  });

  useEffect(() => {
    loadMachineProduction();
  }, []);

  const loadMachineProduction = async () => {
    setLoading(true);
    try {
      const response = await trackingService.getMachineProduction(filters);
      setMachineData(response);
      setMessage({ type: 'success', text: 'Makina üretim verileri yüklendi' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Makina üretim verileri yüklenemedi!' });
      console.error('Makina üretim hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadMachineProduction();
  };

  const clearFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      makina: '',
      operator: ''
    });
    setTimeout(() => loadMachineProduction(), 100);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const blob = await trackingService.exportMachineProductionExcel(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `makina_uretim_${new Date().getTime()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Excel dosyası indirildi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Excel oluşturulamadı!' });
      console.error('Excel export error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setLoading(true);
      const blob = await trackingService.exportMachineProductionPdf(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `makina_uretim_${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'PDF dosyası indirildi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'PDF oluşturulamadı!' });
      console.error('PDF export error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique operators from operator_stats
  const uniqueOperators = machineData?.operator_stats ? Object.keys(machineData.operator_stats).sort() : [];

  return (
    <>
      {/* Summary Cards */}
      {machineData?.summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>📊 Toplam Adet</p>
            <h2 style={{ color: COLORS.primary, margin: 0 }}>{machineData.summary.total_adet}</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>⚖️ Toplam Tonaj</p>
            <h2 style={{ color: '#10b981', margin: 0 }}>{machineData.summary.total_tonaj} kg</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>🏆 En Verimli Makina</p>
            <h2 style={{ color: '#f59e0b', margin: 0 }}>{machineData.summary.most_productive_machine || 'N/A'}</h2>
            <p style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              {machineData.summary.most_productive_tonaj} kg
            </p>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>👥 Aktif Operatör</p>
            <h2 style={{ color: '#6366f1', margin: 0 }}>{machineData.summary.active_operators}</h2>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>🔍 Filtreler</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Başlangıç Tarihi</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Bitiş Tarihi</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Makina</label>
            <select
              value={filters.makina}
              onChange={(e) => handleFilterChange('makina', e.target.value)}
              style={inputStyle}
            >
              <option value="">Tümü</option>
              {machineData?.machines?.map(machine => (
                <option key={machine} value={machine}>{machine}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Operatör</label>
            <select
              value={filters.operator}
              onChange={(e) => handleFilterChange('operator', e.target.value)}
              style={inputStyle}
            >
              <option value="">Tümü</option>
              {uniqueOperators.map(operator => (
                <option key={operator} value={operator}>{operator}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button onClick={applyFilters} style={primaryButtonStyle}>
              {loading ? '🔄 Yükleniyor...' : '🔍 Filtrele'}
            </button>
            <button onClick={clearFilters} style={secondaryButtonStyle}>
              ❌ Temizle
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              onClick={handleExportExcel} 
              disabled={loading || !machineData}
              style={{
                ...exportButtonStyle,
                background: !machineData ? '#ccc' : '#10b981',
                cursor: !machineData ? 'not-allowed' : 'pointer'
              }}
            >
              📊 Excel İndir
            </button>
            <button 
              onClick={handleExportPdf}
              disabled={loading || !machineData}
              style={{
                ...exportButtonStyle,
                background: !machineData ? '#ccc' : '#ef4444',
                cursor: !machineData ? 'not-allowed' : 'pointer'
              }}
            >
              📄 PDF İndir
            </button>
          </div>
        </div>
      </div>

      {/* Machine Production Table */}
      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: COLORS.secondary, color: '#fff' }}>
                <th style={tableHeaderStyle}>Dönem</th>
                {machineData?.machines?.map(machine => (
                  <th key={machine} colSpan="2" style={{...tableHeaderStyle, background: '#333'}}>
                    Makina {machine}
                  </th>
                ))}
                <th colSpan="2" style={{...tableHeaderStyle, background: '#10b981'}}>TOPLAM</th>
              </tr>
              <tr style={{ background: COLORS.primary, color: '#fff' }}>
                <th style={tableHeaderStyle}></th>
                {machineData?.machines?.map(machine => (
                  <React.Fragment key={machine}>
                    <th style={tableHeaderStyle}>Adet</th>
                    <th style={tableHeaderStyle}>Tonaj (kg)</th>
                  </React.Fragment>
                ))}
                <th style={tableHeaderStyle}>Adet</th>
                <th style={tableHeaderStyle}>Tonaj (kg)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={(machineData?.machines?.length || 0) * 2 + 3} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    Yükleniyor...
                  </td>
                </tr>
              ) : !machineData?.monthly_data || machineData.monthly_data.length === 0 ? (
                <tr>
                  <td colSpan={(machineData?.machines?.length || 0) * 2 + 3} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    Veri bulunamadı
                  </td>
                </tr>
              ) : (
                <>
                  {machineData.monthly_data.map((month, index) => {
                    let monthTotalAdet = 0;
                    let monthTotalTonaj = 0;
                    
                    return (
                      <tr key={index} style={{ 
                        borderBottom: '1px solid #e5e7eb',
                        background: index % 2 === 0 ? '#fff' : '#f9fafb'
                      }}>
                        <td style={{...tableCellStyle, fontWeight: '600'}}>{month.month}</td>
                        {machineData.machines.map(machine => {
                          const data = month.machines[machine] || { adet: 0, tonaj: 0 };
                          monthTotalAdet += data.adet;
                          monthTotalTonaj += data.tonaj;
                          return (
                            <React.Fragment key={machine}>
                              <td style={tableCellStyle}>{data.adet}</td>
                              <td style={tableCellStyle}>{data.tonaj.toFixed(2)}</td>
                            </React.Fragment>
                          );
                        })}
                        <td style={{...tableCellStyle, fontWeight: '600', background: '#d1fae5'}}>{monthTotalAdet}</td>
                        <td style={{...tableCellStyle, fontWeight: '600', background: '#d1fae5'}}>{monthTotalTonaj.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  
                  {/* TOPLAM ROW */}
                  <tr style={{ background: '#fef3c7', fontWeight: '600' }}>
                    <td style={{...tableCellStyle, fontWeight: '700'}}>TOPLAM</td>
                    {machineData.machines.map(machine => {
                      const totals = machineData.machine_totals[machine] || { adet: 0, tonaj: 0 };
                      return (
                        <React.Fragment key={machine}>
                          <td style={tableCellStyle}>{totals.adet}</td>
                          <td style={tableCellStyle}>{totals.tonaj.toFixed(2)}</td>
                        </React.Fragment>
                      );
                    })}
                    <td style={{...tableCellStyle, fontWeight: '700', background: '#fee2e2', color: COLORS.primary}}>
                      {machineData.summary.total_adet}
                    </td>
                    <td style={{...tableCellStyle, fontWeight: '700', background: '#fee2e2', color: COLORS.primary}}>
                      {machineData.summary.total_tonaj}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Operator Stats */}
      {machineData?.operator_stats && Object.keys(machineData.operator_stats).length > 0 && (
        <div style={{ background: '#fff', borderRadius: '12px', marginTop: '2rem', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: COLORS.primary }}>👥 Operatör Detayları</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
            {Object.entries(machineData.operator_stats).map(([operator, stats]) => (
              <div key={operator} style={{ 
                padding: '1rem', 
                background: '#f9fafb', 
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <p style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#333' }}>{operator}</p>
                <p style={{ fontSize: '0.9rem', color: '#666', margin: '0.25rem 0' }}>
                  Adet: <strong>{stats.adet}</strong>
                </p>
                <p style={{ fontSize: '0.9rem', color: '#666', margin: '0.25rem 0' }}>
                  Tonaj: <strong>{stats.tonaj.toFixed(2)} kg</strong>
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// Styles
const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  border: '2px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '0.95rem'
};

const primaryButtonStyle = {
  padding: '0.75rem 1.5rem',
  background: 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: '600',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(228, 10, 4, 0.3)'
};

const secondaryButtonStyle = {
  padding: '0.75rem 1.5rem',
  background: '#fff',
  color: '#666',
  border: '2px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: '600',
  cursor: 'pointer'
};

const exportButtonStyle = {
  padding: '0.75rem 1.5rem',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.95rem',
  fontWeight: '600',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  transition: 'all 0.2s'
};

const tableHeaderStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontWeight: '600',
  fontSize: '0.9rem',
  borderBottom: '2px solid rgba(255,255,255,0.2)'
};

const tableCellStyle = {
  padding: '1rem',
  fontSize: '0.9rem',
  color: '#374151',
  textAlign: 'center'
};

export default Tracking;
